import { makeAutoObservable, runInAction } from 'mobx'
import api from '@/services/api'

export interface InboxChannel {
  id: string
  project_id: string
  workspace_id: string
  is_project_channel: boolean
  agent_id?: string
  name: string
  description?: string
  is_archived: boolean
  is_muted: boolean
  message_count: number
  unread_count: number
  last_message_at?: string
}

export interface InboxMessage {
  id: string
  channel_id: string
  project_id: string
  sender_type: 'user' | 'agent' | 'system'
  sender_user_id?: string
  sender_agent_id?: string
  recipient_type?: string
  message_type: string
  content: string
  parent_message_id?: string
  thread_root_id?: string
  reply_count: number
  status: string
  priority: string
  requires_response: boolean
  response_deadline?: string
  decision_options?: Array<{ label: string; value: string; description?: string }>
  user_response?: Record<string, unknown>
  has_attachments: boolean
  attachments?: Array<Record<string, unknown>>
  code_blocks?: Array<{ language: string; code: string; filename?: string }>
  mentioned_user_ids?: string[]
  mentioned_agent_ids?: string[]
  reactions?: Record<string, string[]>
  created_at: string
  updated_at: string
}

export interface InboxNotification {
  id: string
  user_id: string
  project_id: string
  channel_id?: string
  message_id?: string
  notification_type: string
  title: string
  body?: string
  data?: Record<string, unknown>
  is_read: boolean
  is_delivered: boolean
  created_at: string
}

class InboxStore {
  // Channels
  channels: InboxChannel[] = []
  selectedChannelId: string | null = null
  channelLoading = false

  // Messages
  messages: InboxMessage[] = []
  messagesLoading = false
  hasMoreMessages = true

  // Thread
  threadMessages: InboxMessage[] = []
  selectedThreadId: string | null = null
  threadLoading = false

  // Notifications
  notifications: InboxNotification[] = []
  unreadNotificationCount = 0
  notificationsLoading = false

  // Unread counts per channel
  unreadCounts: Record<string, number> = {}

  // UI state
  showNotifications = false
  typingUsers: Record<string, string[]> = {} // channelId -> userIds

  // Project context
  currentProjectId: string | null = null
  currentWorkspaceId: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  // ==========================================================================
  // Channel Operations
  // ==========================================================================

  async fetchProjectChannels(projectId: string): Promise<void> {
    this.channelLoading = true
    try {
      const data = await api.getProjectChannels(projectId, true)
      runInAction(() => {
        this.channels = data as InboxChannel[]
        this.currentProjectId = projectId
        // Select first channel if none selected
        if (!this.selectedChannelId && this.channels.length > 0) {
          this.selectedChannelId = this.channels[0].id
        }
      })
    } catch (error) {
      console.error('Failed to fetch channels:', error)
      throw error
    } finally {
      runInAction(() => {
        this.channelLoading = false
      })
    }
  }

  async fetchUserChannels(workspaceId: string): Promise<void> {
    this.channelLoading = true
    try {
      const data = await api.getUserChannels(workspaceId)
      runInAction(() => {
        this.channels = data.map((item: { channel: InboxChannel; unread_count: number }) => ({
          ...item.channel,
          unread_count: item.unread_count
        }))
        this.currentWorkspaceId = workspaceId
        // Update unread counts
        data.forEach((item: { channel: InboxChannel; unread_count: number }) => {
          this.unreadCounts[item.channel.id] = item.unread_count
        })
      })
    } catch (error) {
      console.error('Failed to fetch user channels:', error)
      throw error
    } finally {
      runInAction(() => {
        this.channelLoading = false
      })
    }
  }

  async createOrGetChannel(projectId: string, workspaceId: string, agentId?: string): Promise<InboxChannel> {
    try {
      const data = await api.createOrGetChannel(projectId, workspaceId, agentId)
      runInAction(() => {
        const existingIndex = this.channels.findIndex(c => c.id === data.id)
        if (existingIndex === -1) {
          this.channels.push(data as InboxChannel)
        }
      })
      return data as InboxChannel
    } catch (error) {
      console.error('Failed to create/get channel:', error)
      throw error
    }
  }

  selectChannel(channelId: string): void {
    this.selectedChannelId = channelId
    this.messages = []
    this.hasMoreMessages = true
    this.selectedThreadId = null
    this.threadMessages = []
  }

  // ==========================================================================
  // Message Operations
  // ==========================================================================

  async fetchMessages(channelId: string, beforeId?: string): Promise<void> {
    this.messagesLoading = true
    try {
      const data = await api.getChannelMessages(channelId, { limit: 50, before_id: beforeId })
      runInAction(() => {
        if (beforeId) {
          // Prepend older messages
          this.messages = [...(data as InboxMessage[]), ...this.messages]
        } else {
          this.messages = data as InboxMessage[]
        }
        this.hasMoreMessages = data.length === 50
      })
    } catch (error) {
      console.error('Failed to fetch messages:', error)
      throw error
    } finally {
      runInAction(() => {
        this.messagesLoading = false
      })
    }
  }

  async sendMessage(
    channelId: string,
    projectId: string,
    content: string,
    options: Partial<{
      message_type: string
      parent_message_id: string
      priority: string
      attachments: Array<Record<string, unknown>>
      code_blocks: Array<{ language: string; code: string }>
      mentioned_user_ids: string[]
      mentioned_agent_ids: string[]
      metadata: Record<string, unknown>
    }> = {}
  ): Promise<InboxMessage> {
    try {
      const data = await api.sendChannelMessage(channelId, projectId, {
        content,
        message_type: options.message_type || 'user_message',
        parent_message_id: options.parent_message_id,
        priority: options.priority || 'normal',
        attachments: options.attachments,
        code_blocks: options.code_blocks,
        mentioned_user_ids: options.mentioned_user_ids,
        mentioned_agent_ids: options.mentioned_agent_ids,
        metadata: options.metadata
      })
      runInAction(() => {
        // Add to messages if in main view
        if (!options.parent_message_id) {
          this.messages.push(data as InboxMessage)
        } else {
          // Add to thread messages
          this.threadMessages.push(data as InboxMessage)
          // Update reply count on parent
          const parentIndex = this.messages.findIndex(m => m.id === options.parent_message_id)
          if (parentIndex !== -1) {
            this.messages[parentIndex].reply_count++
          }
        }
      })
      return data as InboxMessage
    } catch (error) {
      console.error('Failed to send message:', error)
      throw error
    }
  }

  async fetchThreadMessages(threadRootId: string): Promise<void> {
    this.threadLoading = true
    this.selectedThreadId = threadRootId
    try {
      const data = await api.getThreadMessages(threadRootId)
      runInAction(() => {
        this.threadMessages = data as InboxMessage[]
      })
    } catch (error) {
      console.error('Failed to fetch thread messages:', error)
      throw error
    } finally {
      runInAction(() => {
        this.threadLoading = false
      })
    }
  }

  closeThread(): void {
    this.selectedThreadId = null
    this.threadMessages = []
  }

  // ==========================================================================
  // HITL Operations
  // ==========================================================================

  async respondToHITL(messageId: string, response: Record<string, unknown>): Promise<InboxMessage> {
    try {
      const data = await api.respondToHITL(messageId, response)
      runInAction(() => {
        // Update message in list
        const index = this.messages.findIndex(m => m.id === messageId)
        if (index !== -1) {
          this.messages[index] = data as InboxMessage
        }
      })
      return data as InboxMessage
    } catch (error) {
      console.error('Failed to respond to HITL:', error)
      throw error
    }
  }

  // ==========================================================================
  // Reaction Operations
  // ==========================================================================

  async addReaction(messageId: string, emoji: string): Promise<void> {
    try {
      await api.addMessageReaction(messageId, emoji)
      // WebSocket will update the UI
    } catch (error) {
      console.error('Failed to add reaction:', error)
      throw error
    }
  }

  async removeReaction(messageId: string, emoji: string): Promise<void> {
    try {
      await api.removeMessageReaction(messageId, emoji)
      // WebSocket will update the UI
    } catch (error) {
      console.error('Failed to remove reaction:', error)
      throw error
    }
  }

  // ==========================================================================
  // Read Status Operations
  // ==========================================================================

  async markChannelRead(channelId: string): Promise<void> {
    try {
      await api.markChannelRead(channelId)
      runInAction(() => {
        this.unreadCounts[channelId] = 0
        const channel = this.channels.find(c => c.id === channelId)
        if (channel) {
          channel.unread_count = 0
        }
      })
    } catch (error) {
      console.error('Failed to mark channel read:', error)
      throw error
    }
  }

  async fetchUnreadCounts(projectId?: string): Promise<void> {
    try {
      const data = await api.getUnreadCounts(projectId)
      runInAction(() => {
        this.unreadCounts = data.counts
      })
    } catch (error) {
      console.error('Failed to fetch unread counts:', error)
    }
  }

  // ==========================================================================
  // Notification Operations
  // ==========================================================================

  async fetchNotifications(unreadOnly = false): Promise<void> {
    this.notificationsLoading = true
    try {
      const data = await api.getNotifications({ unread_only: unreadOnly, limit: 50 })
      runInAction(() => {
        this.notifications = data as InboxNotification[]
        this.unreadNotificationCount = (data as InboxNotification[]).filter((n: InboxNotification) => !n.is_read).length
      })
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      throw error
    } finally {
      runInAction(() => {
        this.notificationsLoading = false
      })
    }
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    try {
      await api.markNotificationRead(notificationId)
      runInAction(() => {
        const notification = this.notifications.find(n => n.id === notificationId)
        if (notification) {
          notification.is_read = true
          this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1)
        }
      })
    } catch (error) {
      console.error('Failed to mark notification read:', error)
      throw error
    }
  }

  async markAllNotificationsRead(projectId?: string): Promise<void> {
    try {
      await api.markAllNotificationsRead(projectId)
      runInAction(() => {
        this.notifications.forEach(n => {
          if (!projectId || n.project_id === projectId) {
            n.is_read = true
          }
        })
        this.unreadNotificationCount = projectId
          ? this.notifications.filter(n => !n.is_read).length
          : 0
      })
    } catch (error) {
      console.error('Failed to mark all notifications read:', error)
      throw error
    }
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications
    if (this.showNotifications && this.notifications.length === 0) {
      this.fetchNotifications()
    }
  }

  // ==========================================================================
  // WebSocket Event Handlers
  // ==========================================================================

  handleNewMessage(message: InboxMessage): void {
    // Add to messages if viewing this channel
    if (message.channel_id === this.selectedChannelId && !message.parent_message_id) {
      this.messages.push(message)
    }
    // Add to thread if viewing this thread
    if (message.thread_root_id === this.selectedThreadId) {
      this.threadMessages.push(message)
    }
    // Update unread count if not viewing this channel
    if (message.channel_id !== this.selectedChannelId) {
      this.unreadCounts[message.channel_id] = (this.unreadCounts[message.channel_id] || 0) + 1
      const channel = this.channels.find(c => c.id === message.channel_id)
      if (channel) {
        channel.unread_count++
      }
    }
  }

  handleHITLResponse(messageId: string, response: Record<string, unknown>): void {
    const message = this.messages.find(m => m.id === messageId)
    if (message) {
      message.user_response = response
      message.status = 'responded'
    }
  }

  handleReaction(messageId: string, emoji: string, userId: string, action: 'add' | 'remove'): void {
    const message = this.messages.find(m => m.id === messageId) ||
                    this.threadMessages.find(m => m.id === messageId)
    if (message) {
      if (!message.reactions) {
        message.reactions = {}
      }
      if (action === 'add') {
        if (!message.reactions[emoji]) {
          message.reactions[emoji] = []
        }
        if (!message.reactions[emoji].includes(userId)) {
          message.reactions[emoji].push(userId)
        }
      } else {
        if (message.reactions[emoji]) {
          message.reactions[emoji] = message.reactions[emoji].filter(id => id !== userId)
          if (message.reactions[emoji].length === 0) {
            delete message.reactions[emoji]
          }
        }
      }
    }
  }

  handleTyping(channelId: string, userId: string, isTyping: boolean): void {
    if (!this.typingUsers[channelId]) {
      this.typingUsers[channelId] = []
    }
    if (isTyping) {
      if (!this.typingUsers[channelId].includes(userId)) {
        this.typingUsers[channelId].push(userId)
      }
    } else {
      this.typingUsers[channelId] = this.typingUsers[channelId].filter(id => id !== userId)
    }
  }

  // ==========================================================================
  // Computed Properties
  // ==========================================================================

  get selectedChannel(): InboxChannel | undefined {
    return this.channels.find(c => c.id === this.selectedChannelId)
  }

  get projectChannels(): InboxChannel[] {
    return this.channels.filter(c => c.is_project_channel)
  }

  get agentChannels(): InboxChannel[] {
    return this.channels.filter(c => !c.is_project_channel)
  }

  get totalUnreadCount(): number {
    return Object.values(this.unreadCounts).reduce((sum, count) => sum + count, 0)
  }

  get hitlPendingMessages(): InboxMessage[] {
    return this.messages.filter(m => m.requires_response && !m.user_response)
  }

  // ==========================================================================
  // Reset
  // ==========================================================================

  reset(): void {
    this.channels = []
    this.selectedChannelId = null
    this.messages = []
    this.threadMessages = []
    this.selectedThreadId = null
    this.notifications = []
    this.unreadCounts = {}
    this.currentProjectId = null
    this.currentWorkspaceId = null
  }
}

export const inboxStore = new InboxStore()
